import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, CheckCircle2, XCircle, CpuIcon, ChevronRight } from 'lucide-react';
import { Provider, MOCK_PROVIDERS, formatCurrency, getInitials, getProviderTotals } from '../data/providers';
export function ProvidersPage() {
  const [providers] = useState<Provider[]>(MOCK_PROVIDERS);
  const navigate = useNavigate();
  // Calculate global KPIs
  const kpis = useMemo(() => {
    const totalProviders = providers.length;
    const activeProviders = providers.filter(p => p.status === 'Active').length;
    let globalBudget = 0;
    let globalSpent = 0;
    providers.forEach(p => {
      const {
        totalBudget,
        totalSpent
      } = getProviderTotals(p);
      globalBudget += totalBudget;
      globalSpent += totalSpent;
    });
    const globalPercent = globalBudget > 0 ? Math.round(globalSpent / globalBudget * 100) : 0;
    return {
      totalProviders,
      activeProviders,
      globalBudget,
      globalSpent,
      globalPercent
    };
  }, [providers]);
  return <div className="min-h-screen bg-[#F4F6F8] p-4 md:p-8" data-id="element-4167">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8" data-id="element-4168">
        <h1 className="text-[28px] md:text-[32px] font-semibold text-[#1A202C] tracking-tight" data-id="element-4169">
          Provider
        </h1>
        <button className="flex items-center gap-2 px-4 py-2.5 bg-[#2A3B5C] text-white rounded-lg hover:bg-[#1E2A40] transition-colors shadow-sm" data-id="element-4170">
          <Plus className="w-4 h-4" data-id="element-4171" />
          <span className="text-[13px] font-medium" data-id="element-4172">Aggiungi Provider</span>
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-id="element-4173">
        {/* Card 1 */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex flex-col" data-id="element-4174">
          <h3 className="text-[13px] font-medium text-gray-500 mb-3" data-id="element-4175">
            Provider Totali
          </h3>
          <div className="flex items-baseline gap-2 mt-auto" data-id="element-4176">
            <span className="text-[28px] font-bold text-[#1A202C]" data-id="element-4177">
              {kpis.totalProviders}
            </span>
          </div>
        </div>

        {/* Card 2 */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex flex-col" data-id="element-4178">
          <h3 className="text-[13px] font-medium text-gray-500 mb-3" data-id="element-4179">
            Provider Attivi
          </h3>
          <div className="flex items-baseline gap-2 mt-auto" data-id="element-4180">
            <span className="text-[28px] font-bold text-[#1A202C]" data-id="element-4181">
              {kpis.activeProviders}
            </span>
          </div>
        </div>

        {/* Card 3 */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex flex-col" data-id="element-4182">
          <h3 className="text-[13px] font-medium text-gray-500 mb-3" data-id="element-4183">
            Budget Totale
          </h3>
          <div className="flex items-baseline gap-2 mt-auto" data-id="element-4184">
            <span className="text-[28px] font-bold text-[#1A202C]" data-id="element-4185">
              {formatCurrency(kpis.globalBudget)}
            </span>
          </div>
        </div>

        {/* Card 4 */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex flex-col" data-id="element-4186">
          <div className="flex justify-between items-center mb-3" data-id="element-4187">
            <h3 className="text-[13px] font-medium text-gray-500" data-id="element-4188">
              Spesa Totale
            </h3>
            <span className="text-[11px] font-medium text-gray-400" data-id="element-4189">
              {kpis.globalPercent}%
            </span>
          </div>
          <div className="mt-auto" data-id="element-4190">
            <div className="flex items-baseline gap-1 mb-2" data-id="element-4191">
              <span className="text-[28px] font-bold text-[#1A202C]" data-id="element-4192">
                {formatCurrency(kpis.globalSpent)}
              </span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-1.5" data-id="element-4193">
              <div className={`h-1.5 rounded-full ${kpis.globalPercent >= 80 ? 'bg-amber-500' : 'bg-[#00B4D8]'}`} style={{
              width: `${kpis.globalPercent}%`
            }} data-id="element-4194"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Provider List */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col divide-y divide-gray-100" data-id="element-4195">
        {providers.map(provider => {
        const {
          totalBudget,
          totalSpent,
          percent
        } = getProviderTotals(provider);
        const isNearLimit = percent >= 80;
        return <button key={provider.id} onClick={() => navigate(`/providers/${provider.id}`)} className="w-full text-left p-4 md:p-5 hover:bg-gray-50 transition-colors group flex flex-col md:flex-row md:items-center gap-4 md:gap-6" data-id="element-4196">
              {/* Logo & Name */}
              <div className="flex items-center gap-4 md:w-1/4 shrink-0" data-id="element-4197">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center border border-gray-100 shadow-sm bg-white overflow-hidden shrink-0" style={{
              backgroundColor: provider.logo ? 'white' : `${provider.color}15`
            }} data-id="element-4198">
                  {provider.logo ? <img src={provider.logo} alt={provider.name} className="w-6 h-6 object-contain" data-id="element-4199" /> : <span className="text-[13px] font-bold" style={{
                color: provider.color
              }} data-id="element-4200">
                      {getInitials(provider.name)}
                    </span>}
                </div>
                <div className="min-w-0" data-id="element-4201">
                  <h3 className="text-[14px] font-semibold text-[#1A202C] truncate group-hover:text-[#00B4D8] transition-colors" data-id="element-4202">
                    {provider.name}
                  </h3>
                  <div className="md:hidden mt-1" data-id="element-4203">
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-medium ${provider.status === 'Active' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-gray-100 text-gray-500 border border-gray-200'}`} data-id="element-4204">
                      {provider.status === 'Active' ? <CheckCircle2 className="w-2.5 h-2.5" data-id="element-4205" /> : <XCircle className="w-2.5 h-2.5" data-id="element-4206" />}
                      {provider.status === 'Active' ? 'Attivo' : 'Inattivo'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Status (Desktop) */}
              <div className="hidden md:flex md:w-1/6 shrink-0" data-id="element-4207">
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium ${provider.status === 'Active' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-gray-100 text-gray-500 border border-gray-200'}`} data-id="element-4208">
                  {provider.status === 'Active' ? <CheckCircle2 className="w-3 h-3" data-id="element-4209" /> : <XCircle className="w-3 h-3" data-id="element-4210" />}
                  {provider.status === 'Active' ? 'Attivo' : 'Inattivo'}
                </span>
              </div>

              {/* Models */}
              <div className="flex items-center gap-1.5 md:w-1/6 shrink-0" data-id="element-4211">
                <CpuIcon className="w-4 h-4 text-gray-400" data-id="element-4212" />
                <span className="text-[13px] text-gray-600" data-id="element-4213">
                  {provider.models.length}{' '}
                  {provider.models.length === 1 ? 'modello' : 'modelli'}
                </span>
              </div>

              {/* Budget */}
              <div className="flex-1 min-w-[200px]" data-id="element-4214">
                <div className="flex items-center justify-between text-[12px] mb-1.5" data-id="element-4215">
                  <span className="font-medium text-[#1A202C]" data-id="element-4216">
                    {formatCurrency(totalSpent)}
                  </span>
                  <span className="text-gray-500" data-id="element-4217">
                    su {formatCurrency(totalBudget)}
                  </span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-1.5" data-id="element-4218">
                  <div className={`h-1.5 rounded-full transition-all ${isNearLimit ? 'bg-amber-500' : 'bg-[#00B4D8]'}`} style={{
                width: `${percent}%`
              }} data-id="element-4219" />
                </div>
              </div>

              {/* Chevron */}
              <div className="hidden md:flex shrink-0 pl-4" data-id="element-4220">
                <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-[#00B4D8] transition-colors" data-id="element-4221" />
              </div>
            </button>;
      })}
      </div>
    </div>;
}